import React, { useState, useRef, useEffect } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { plyxApi, plyxMediaUrl } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Loader2, Camera, Save, Wallet } from "lucide-react";
import { toast } from "sonner";

const PlyxSettings = () => {
  const { user, loading, refresh } = usePlyxAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [form, setForm] = useState({ display_name: "", bio: "", country: "", address: "" });

  useEffect(() => {
    if (user) setForm({
      display_name: user.display_name || "",
      bio: user.bio || "",
      country: user.country || "",
      address: user.address_text || "",
    });
  }, [user]);

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin"/></div>;
  }
  if (!user) {
    return <Navigate to="/plyx/login" replace state={{ from: "/plyx/settings" }}/>;
  }

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await plyxApi.patch("/plyx/me", form);
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSavingProfile(false);
    }
  };

  const onAvatarPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Pick an image"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("avatar", f);
      await plyxApi.post("/plyx/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Avatar updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingAvatar(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="plyx-settings-page">
      <div className="container-narrow py-10 max-w-2xl">
        <h1 className="font-display text-4xl sm:text-5xl mb-2 tracking-tight">Edit profile</h1>
        <p className="text-white/60 mb-8">@{user.handle}</p>

        {/* Avatar */}
        <div className="flex items-center gap-5 p-5 rounded-2xl bg-white/5 border border-white/10 mb-6">
          <div className="w-20 h-20 rounded-full bg-white/10 overflow-hidden flex items-center justify-center text-2xl font-display shrink-0">
            {user.avatar_url
              ? <img src={plyxMediaUrl(user.avatar_url)} alt="" className="w-full h-full object-cover"/>
              : user.display_name?.slice(0, 1) || "?"}
          </div>
          <div className="flex-1">
            <div className="font-semibold mb-1">Profile picture</div>
            <div className="text-xs text-white/50 mb-2">JPG or PNG, max 5MB</div>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploadingAvatar}
              data-testid="plyx-avatar-upload-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-[#0A0A0A] disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
            >
              {uploadingAvatar ? <Loader2 className="w-4 h-4 animate-spin"/> : <Camera className="w-4 h-4"/>}
              {uploadingAvatar ? "Uploading…" : "Change photo"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onAvatarPick} data-testid="plyx-avatar-file-input"/>
          </div>
        </div>

        {/* Profile fields */}
        <form onSubmit={saveProfile} className="space-y-4 p-5 rounded-2xl bg-white/5 border border-white/10">
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Display name</label>
            <input
              maxLength={60}
              value={form.display_name}
              onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
              data-testid="plyx-settings-display-name"
              className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Bio</label>
            <textarea
              rows={3}
              maxLength={300}
              value={form.bio}
              onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
              data-testid="plyx-settings-bio"
              className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
            />
            <div className="text-xs text-white/40 mt-1">{form.bio.length}/300</div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Country</label>
            <input
              maxLength={60}
              value={form.country}
              onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
              data-testid="plyx-settings-country"
              className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Delivery address</label>
            {!user.address_text && (
              <div className="text-xs text-[#FF6B6B] mb-1.5" data-testid="plyx-address-missing-warning">
                ⚠️ No delivery address on file — add one so your orders reach you.
              </div>
            )}
            <textarea
              rows={3}
              maxLength={400}
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              data-testid="plyx-settings-address"
              placeholder="House number, street, city, state/region, postcode"
              className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
            />
          </div>
          <button
            type="submit"
            disabled={savingProfile}
            data-testid="plyx-settings-save"
            className="w-full py-3 rounded-full font-semibold text-[#0A0A0A] disabled:opacity-50 inline-flex items-center justify-center gap-2"
            style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
          >
            {savingProfile ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
            Save profile
          </button>
        </form>

        {/* Wallet entry */}
        <Link
          to="/plyx/wallet"
          data-testid="plyx-settings-wallet-link"
          className="mt-6 flex items-center justify-between p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/8 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Wallet className="w-5 h-5 text-[#FFB42D]"/>
            <div>
              <div className="font-semibold">Watch-to-earn wallet</div>
              <div className="text-xs text-white/50">See your Plyx credits</div>
            </div>
          </div>
          <span className="text-white/40">›</span>
        </Link>

        <button
          type="button"
          onClick={() => navigate(`/plyx/u/${user.handle}`)}
          data-testid="plyx-settings-view-profile"
          className="mt-6 text-sm text-white/60 hover:text-white underline"
        >
          ← Back to my profile
        </button>
      </div>
    </div>
  );
};

export default PlyxSettings;
